import { Scene } from 'phaser';
import * as Phaser from 'phaser';
import { LevelLoader, LoadedLevel } from '../core/LevelLoader';
import type { LevelData } from '../data/LevelFormat';
import { DataManagerEntity, EntityFactory, RuntimeManagerSpawn, LevelSpawnData, DataManagerCollision, CollisionSystem, AnimationTriggerSystem, EffectsSystem, DataManagerImagePoint, AbductionEffects, EntityClass, RuntimeManagerSoundEvent } from '../systems';
import { TractorBeamBehavior } from '../entities/behaviors/TractorBeamBehavior';
import { HealthPowerUp } from '../entities/powerups/HealthPowerUp';
import { BiomoneyPowerUp } from '../entities/powerups/BiomoneyPowerUp';
import { AmmoPowerUp } from '../entities/powerups/AmmoPowerUp';
import { PowerUp } from '../entities/powerups/PowerUp';
import { ParticleData } from '../data/ParticleData';
import { ParticleRegistry } from '../systems/ParticleRegistry';
import { Player, Subjefelevel1 } from '../entities';
import { RuntimeManagerProjectile } from '../projectiles';
import { DataManagerProjectile } from '../projectiles/DataManagerProjectile';
import { DataManagerWeapon } from '../weapons/DataManagerWeapon';
import { DataManagerPowerUp } from '../data/DataManagerPowerUp';
import { WeaponButtonsBar } from '../ui/WeaponButtonsBar';
import { TractorBeam } from '../entities/TractorBeam';
import { CollisionDebugOverlay, RangeDebugOverlay, ObjectInspectorOverlay, DebugManagerHotReload } from '../debug';
import { storage, WeaponState } from '../core/Storage';
import { audioManager } from '../core/CoreManagerAudio';
import { PausePopup } from '../ui/PausePopup';
import { DeathPopup } from '../ui/DeathPopup';
import { BossHealthBar } from '../ui/BossHealthBar';
import { FloatingNotification } from '../ui/FloatingNotification';
import { ShooterBehavior } from '../entities/behaviors/ShooterBehavior';
import { SpawnChildrenBehavior } from '../entities/behaviors/SpawnChildrenBehavior';
import { LayerDepths } from '../constants/enums';
import { Behavior } from '../entities/behaviors/Behavior';
import { initLocalization, t } from '../i18n/Localization';

export class Game extends Scene {
  // Level system
  private levelId: string = 'level1';
  private levelLoader!: LevelLoader;
  private loadedLevel: LoadedLevel | null = null;

  // Entity systems
  private prefabManager!: DataManagerEntity;
  private entityFactory!: EntityFactory;
  private spawnManager!: RuntimeManagerSpawn;
  private collisionDataManager!: DataManagerCollision;
  private imagePointManager!: DataManagerImagePoint;
  private collisionSystem!: CollisionSystem;
  private animationTriggerSystem!: AnimationTriggerSystem;
  private effectsSystem!: EffectsSystem;
  private soundEventManager!: RuntimeManagerSoundEvent;

  // Camera
  private camera!: Phaser.Cameras.Scene2D.Camera;

  // Player
  private player!: Player;

  // UI
  private levelInfoText!: Phaser.GameObjects.Text;
  private backButton!: Phaser.GameObjects.Text;
  private devInstructionsText: Phaser.GameObjects.Text | null = null;
  private weaponButtons!: WeaponButtonsBar;

  // HUD
  private healthContainer!: Phaser.GameObjects.Container;
  private healthHearts: Phaser.GameObjects.Image[] = [];
  private healthBg!: Phaser.GameObjects.Graphics;
  private biomatterIcon!: Phaser.GameObjects.Image;
  private biomatterText!: Phaser.GameObjects.Text;
  private currentBiomatter: number = 0;
  private lastHealthDisplay: number = -1;

  // Projectile system
  private projectileManager!: RuntimeManagerProjectile;

  // Tractor beam visual and behavior
  private tractorBeam!: TractorBeam;
  private tractorBeamBehavior!: TractorBeamBehavior;
  private abductionEffects!: AbductionEffects;

  // Debug overlays
  private collisionDebug!: CollisionDebugOverlay;
  private rangeDebug!: RangeDebugOverlay;
  private objectInspector!: ObjectInspectorOverlay;

  // Ground collision
  private groundCollider!: Phaser.Physics.Arcade.Collider;
  private playerCollisionEnabled: boolean = true;

  // Pause system
  private pausePopup!: PausePopup;
  private isPaused: boolean = false;

  // Death popup
  private deathPopup!: DeathPopup;

  // Boss UI
  private bossHealthBar!: BossHealthBar;

  // Particle debug display
  private particleDebugText!: Phaser.GameObjects.Text;
  private particleDebugEnabled: boolean = false;
  private bossDefeatedCount: number = 0;
  private victoryArmed: boolean = false;
  private hasWon: boolean = false;
  private levelWinConfig: {
    bossesRequired: number;
    edgeThreshold: number;
    title?: string;
    subtitle?: string;
    continueText?: string;
    replayText?: string;
  } = { bossesRequired: 0, edgeThreshold: 24 };

  // Event handlers (arrow function properties for proper cleanup)
  private handleSpawnEntity = (data: { entityId: string; x: number; y: number; velocityX?: number; velocityY?: number }): void => {
    const entity = this.spawnManager.spawnEntityAt(data.entityId, data.x, data.y);
    if (entity && entity.body) {
      const body = entity.body as Phaser.Physics.Arcade.Body;
      if (data.velocityX !== undefined) body.setVelocityX(data.velocityX);
      if (data.velocityY !== undefined) body.setVelocityY(data.velocityY);
    }
  };

  private handleSpawnDrop = (data: { type: string; x: number; y: number; data?: Record<string, unknown> }): void => {
    // console.log(`[Game] Spawning drop: ${data.type} at (${data.x}, ${data.y})`);
    const powerup = this.createDropPowerUp(data.type, data.x, data.y, data.data);
    if (powerup) {
      if (!this.collisionSystem) {
        console.error('[Game.handleSpawnDrop] CollisionSystem not found', { dropType: data.type });
        return;
      }
      this.collisionSystem.getEnemyEntities().add(powerup);
    }
  };

  private handleShowBossBar = (data: { boss: import('../entities/base/Boss').Boss; name: string; portrait?: string }): void => {
    console.log(`[Game] Show boss bar: ${data.name}, portrait: ${data.portrait}`);
    if (!this.bossHealthBar) {
      console.error('[Game.handleShowBossBar] BossHealthBar not initialized', { bossName: data.name });
      return;
    }
    this.bossHealthBar.show(data.boss, data.name, data.portrait);
  };

  private handleBossDefeated = (): void => {
    console.log('[Game] Boss defeated event received');
    this.bossDefeatedCount += 1;
    if (this.bossDefeatedCount >= this.levelWinConfig.bossesRequired) {
      this.victoryArmed = true;
      FloatingNotification.success(this, 'Escape to the right edge!', {
        position: { x: this.scale.width / 2, y: 120 },
        isHUD: true,
        fontSize: 24,
      });
    }
    if (!this.bossHealthBar) {
      console.error('[Game.handleBossDefeated] BossHealthBar not initialized');
      return;
    }
    this.bossHealthBar.hide('bossDefeated event');
  };

  private handleDestroyObjectsOfType = (data: { type: string }): void => {
    console.log(`[Game] Destroying all objects of type: ${data.type}`);
    const entities = this.spawnManager.getActiveEntities();
    for (const entity of entities) {
      const entityAny = entity as import('../entities/base/Entity').Entity & { prefabName?: string };
      if (entityAny.prefabName === data.type || entity.texture?.key === data.type.toLowerCase()) {
        entity.destroy();
      }
    }
  };

  constructor() {
    super('Game');
  }

  init(data: { levelId?: string }) {
    // Recibir levelId desde la escena anterior (LevelSelect)
    this.levelId = data.levelId || 'level1';
    this.bossDefeatedCount = 0;
    this.victoryArmed = false;
    this.hasWon = false;
  }

  preload() {
    // Precargar el JSON del nivel
    this.levelLoader = new LevelLoader(this);
    this.levelLoader.preloadLevel(this.levelId);

    // Precargar entidades para el RuntimeManagerSpawn
    this.load.json(`${this.levelId}-entities-c3data`, `/assets/levels/${this.levelId}-entities-c3data.json`);

    // Precargar datos de colisión (c3data + overrides)
    this.load.json('collision-c3data', '/assets/data/collision-c3data.json');
    this.load.json('collision-overrides', '/assets/data/collision-overrides.json');

    // Precargar datos de layouts (para behaviors.solid de TileSprites)
    this.load.json('layouts-c3data', '/assets/data/layouts-c3data.json');

    // Precargar datos de animation triggers (c3data + overrides)
    this.load.json('animation-triggers-c3data', '/assets/data/animation-triggers-c3data.json');
    this.load.json('animation-triggers-overrides', '/assets/data/animation-triggers-overrides.json');

    // Precargar datos de entidades (consolidado en build-time)
    this.load.json('entities-c3data', '/assets/data/entities-c3data.json');
    // Overrides manuales para ajustes visuales/posición
    this.load.json('entities-overrides', '/assets/data/entities-overrides.json');
    // Level-specific overrides (layout + entities)
    this.load.json(`${this.levelId}-layout-overrides`, `/assets/levels/${this.levelId}-layout-overrides.json`);
    this.load.json(`${this.levelId}-entities-overrides`, `/assets/levels/${this.levelId}-entities-overrides.json`);

    // Image points data (for spawn positions in stores, weapon positions, etc.)
    this.load.json('imagepoints-c3data', '/assets/data/imagepoints-c3data.json');
    this.load.json('imagepoints-overrides', '/assets/data/imagepoints-overrides.json');

    // Precargar datos de proyectiles (origins, etc.) desde C3
    DataManagerProjectile.preload(this);

    // Precargar datos de armas (guns, bombs, tractors)
    DataManagerWeapon.preload(this);

    // Precargar datos de powerups (efectos data-driven)
    DataManagerPowerUp.preload(this);
    this.load.json('level-win-config', '/assets/data/level-win-config.json');
  }

  async create() {
    // i18n: por si entramos directo a Level 1 (ej. refresh), nos aseguramos de inicializar el diccionario.
    initLocalization(this);

    // Configurar física
    this.physics.world.setBoundsCollision(true, true, true, true);

    // Configurar cámara
    this.camera = this.cameras.main;

    // Initialize core collision systems FIRST (before level load)
    // This allows LevelLoader to register solid TileSprites
    this.setupCoreCollisionSystems();

    // Cargar y renderizar el nivel
    // Now TileSprites with solid behavior will be registered in CollisionSystem
    try {
      this.loadedLevel = await this.levelLoader.loadLevel(this.levelId);
      this.setupCamera(this.loadedLevel);
      await this.createUI(this.loadedLevel.data);
    } catch (error) {
      console.error('Error loading level:', error);
      this.showErrorMessage();
      return;
    }

    // Initialize entity systems (DataManagerEntity, EntityFactory, RuntimeManagerSpawn)
    this.setupEntitySystems();

    // Initialize sound event manager (data-driven audio)
    this.soundEventManager = new RuntimeManagerSoundEvent(this, { debug: false });

    // Initialize projectile system
    this.setupProjectileSystem();

    // Create player
    this.createPlayer(this.loadedLevel.data);
    this.player.setDebugInvulnerable(await storage.isDevModeEnabled());

    // Initialize collision system (after player and projectile system)
    this.setupCollisionSystem();

    // Initialize HUD health display
    this.updateHealthDisplay();

    // Setup ground collision (uses zone from CollisionSystem)
    this.setupGroundCollision();

    // Create weapon buttons UI
    this.createWeaponButtons();

    // Listen for weapon swap failures
    this.events.on('swapFailed', this.showSwapFailedNotification, this);

    // Create tractor beam visual
    this.createTractorBeam();

    // Setup tractor beam system (connects beam visual with abduction behavior)
    this.setupTractorBeamBehavior();

    // Configurar controles de cámara para debug (Q/E zoom)
    this.setupCameraControls();

    // Create debug overlays (SHIFT+F1/F2/F3)
    this.createCollisionDebug();
    this.createRangeDebug();
    this.createObjectInspector();

    // Create pause popup (ESC to toggle)
    this.createPausePopup();

    // Create death popup
    this.createDeathPopup();

    // Create boss health bar
    this.createBossHealthBar();

    // Start level music
    const levelNum = parseInt(this.levelId.replace('level', ''));
    if (levelNum >= 1 && levelNum <= 7) {
      audioManager.playMusicForScene(`Level${levelNum}`);
    }

    // Cleanup on scene shutdown - only remove event handlers
    // Let Phaser handle destruction of GameObjects and Groups automatically
    this.events.once('shutdown', () => {
      // Defensive: remove any undefined entries from display list to avoid shutdown crash
      // This prevents Phaser DisplayList.shutdown from calling destroy on undefined.
      this.children.list = this.children.list.filter(Boolean) as Phaser.GameObjects.GameObject[];

      // Remove event handlers to prevent memory leaks
      this.events.off('swapFailed', this.showSwapFailedNotification, this);
      this.events.off('spawnEntity', this.handleSpawnEntity);
      this.events.off('spawnDrop', this.handleSpawnDrop);
      this.events.off('showBossBar', this.handleShowBossBar);
      this.events.off('bossDefeated', this.handleBossDefeated);
      this.events.off('destroyObjectsOfType', this.handleDestroyObjectsOfType);
    });
  }

  /**
   * Initialize core collision systems (DataManagerCollision + CollisionSystem)
   * Called BEFORE level load so TileSprites can register as solids
   */
  private setupCoreCollisionSystems(): void {
    // Create DataManagerCollision and load collision data
    this.collisionDataManager = new DataManagerCollision(this);
    this.collisionDataManager.loadFromCache(); // Loads collision-c3data + collision-overrides
    this.registry.set('collisionManager', this.collisionDataManager);

    // Create CollisionSystem (without projectile colliders yet)
    this.collisionSystem = new CollisionSystem(this);
    this.registry.set('collisionSystem', this.collisionSystem);
  }

  /**
   * Initialize entity spawn systems
   */
  private setupEntitySystems(): void {
    // Create DataManagerEntity and load consolidated entities-c3data.json
    this.prefabManager = new DataManagerEntity(this);
    this.prefabManager.loadFromCache('entities-c3data');
    this.prefabManager.loadOverrides('entities-overrides');
    this.prefabManager.setCurrentLevel(this.levelId);

    // Note: DataManagerCollision already created in setupCoreCollisionSystems()

    // Create DataManagerImagePoint and load image points
    this.imagePointManager = new DataManagerImagePoint();
    this.imagePointManager.load(this);

    // Create AnimationTriggerSystem and load animation triggers (c3data + overrides)
    this.animationTriggerSystem = new AnimationTriggerSystem(this);
    this.animationTriggerSystem.loadFromCache(); // Loads animation-triggers-c3data + overrides

    // Create EffectsSystem for data-driven effect spawning
    this.effectsSystem = new EffectsSystem(this);

    // Load particle data for particle effects
    ParticleData.load(this);

    // Register in scene registry for access by Projectile and other systems
    // Note: collisionManager already registered in setupCoreCollisionSystems()
    this.registry.set('imagePointManager', this.imagePointManager);
    this.registry.set('animationTriggers', this.animationTriggerSystem);
    this.registry.set('effectsSystem', this.effectsSystem);

    // Create EntityFactory with collision data
    this.entityFactory = new EntityFactory(this, this.prefabManager);
    this.entityFactory.setCollisionManager(this.collisionDataManager);

    // Register EntityFactory in registry for access by custom entities (e.g., Farm1Cannon)
    this.registry.set('entityFactory', this.entityFactory);

    // Register boss classes
    this.entityFactory.register('subjefelevel1', Subjefelevel1 as unknown as EntityClass);

    // Create RuntimeManagerSpawn
    this.spawnManager = new RuntimeManagerSpawn(this, this.entityFactory, {
      spawnAhead: 800,
      despawnBehind: 400,
      cullMargin: 200,
    });

    // Enable debug for spawn verification
    this.spawnManager.setDebug(true);

    // Load level spawn data if available
    const spawnDataKey = `${this.levelId}-entities-c3data`;
    const spawnData = this.cache.json.get(spawnDataKey) as LevelSpawnData | undefined;
    if (spawnData) {
      // Merge additions from level-specific overrides (persistent entities not in auto-generated JSON)
      const levelOverrides = this.cache.json.get(`${this.levelId}-entities-overrides`) as {
        additions?: {
          entities?: Array<{ id: string; entityId: string; x: number; y: number; [key: string]: unknown }>;
        };
        instanceOverrides?: Record<string, Record<string, unknown>>;
      } | undefined;

      const additions = levelOverrides?.additions?.entities;
      if (additions && additions.length > 0) {
        // Ensure entities array exists
        if (!spawnData.entities) {
          spawnData.entities = [];
        }
        // Add entities from overrides (avoid duplicates by ID)
        // Filter out comment entries that don't have id/entityId
        const existingIds = new Set(spawnData.entities.map(e => e.id));
        for (const addition of additions) {
          if (addition.id && addition.entityId && !existingIds.has(addition.id)) {
            spawnData.entities.push(addition);
          }
        }
      }

      // Overrides por instancia (ej: #118) sin tocar archivos c3data
      const instanceOverrides = levelOverrides?.instanceOverrides;
      if (instanceOverrides && spawnData.entities && spawnData.entities.length > 0) {
        spawnData.entities = spawnData.entities.map((entity) => {
          const override = entity.id ? instanceOverrides[entity.id] : undefined;
          if (!override) return entity;

          return {
            ...entity,
            override: {
              ...((entity.override as Record<string, unknown> | undefined) ?? {}),
              ...override,
            },
          };
        });
      }

      this.spawnManager.loadLevel(spawnData);
    }

    // Listen for entity death events
    this.events.on('entityDied', (data: { entity: import('../entities/base/Entity').Entity }) => {
      // Unregister from collision system
      if (this.collisionSystem) {
        this.collisionSystem.unregisterEntity(data.entity);
      }
      this.spawnManager.onEntityDeath(data.entity);
    });

    // Listen for entity spawn events
    this.events.on('entitySpawned', (entity: import('../entities/base/Entity').Entity) => {
      // Register with collision system
      if (this.collisionSystem) {
        this.collisionSystem.registerEntity(entity);
      }

      // Trigger SpawnChildrenBehavior to spawn child entities (e.g., CannonBase → CannonCannon)
      const spawner = entity.getBehavior(SpawnChildrenBehavior);
      if (spawner && !spawner.hasSpawnedChildren()) {
        spawner.spawn();
      }

      // Enable shooter debug if globally enabled
      if (this.shooterDebugEnabled) {
        const entityAny = entity as unknown as { behaviors?: Behavior[] };
        const shooterBehavior = entityAny.behaviors?.find(b => b instanceof ShooterBehavior) as ShooterBehavior | undefined;
        if (shooterBehavior) {
          shooterBehavior.setDebug(true);
        }
      }
    });

    // Listen for dynamic entity spawn requests (from bosses, etc.)
    this.events.on('spawnEntity', this.handleSpawnEntity);

    // Listen for drop spawn requests (powerups, items)
    // NOTE: Falling is handled automatically by PlatformBehavior if entity spawns in air
    this.events.on('spawnDrop', this.handleSpawnDrop);

    // Listen for boss bar display requests
    this.events.on('showBossBar', this.handleShowBossBar);

    // Listen for boss defeated events
    this.events.on('bossDefeated', this.handleBossDefeated);

    // Listen for destroy objects by type requests
    this.events.on('destroyObjectsOfType', this.handleDestroyObjectsOfType);
  }

  /**
   * Create a powerup from a drop type
   * Maps drop types from structure drops to actual powerup classes
   */
  private createDropPowerUp(type: string, x: number, y: number, data?: Record<string, unknown>): PowerUp | null {
    let powerup: PowerUp | null = null;

    switch (type) {
      case 'health':
        powerup = new HealthPowerUp(this, x, y);
        break;

      case 'biomoney':
        // Usar sprite global siempre disponible para evitar missing texture en drops dinámicos
        powerup = new BiomoneyPowerUp(this, x, y, 'hud_biomoney', {
          amount: (data?.amount as number | undefined) ?? (data?.ammo as number | undefined),
        });
        break;

      case 'bomb':
        // Alias for plasmabomb
        powerup = this.createAmmoPowerUpFromWeaponId('plasmabomb', x, y, data?.ammo as number | undefined);
        break;

      default:
        // Check if it's a known ammo type via DataManagerPowerUp
        if (DataManagerPowerUp.isAmmoType(type)) {
          powerup = this.createAmmoPowerUpFromWeaponId(type, x, y, data?.ammo as number | undefined);
        } else {
          console.warn(`[Game.createDropPowerUp] Unknown drop type: ${type}`);
        }
        break;
    }

    return powerup;
  }

  /**
   * Create an AmmoPowerUp using DataManagerPowerUp config
   */
  private createAmmoPowerUpFromWeaponId(weaponId: string, x: number, y: number, ammoOverride?: number): AmmoPowerUp | null {
    const config = DataManagerPowerUp.getAmmoConfig(weaponId);
    if (!config) {
      console.error(`[Game.createAmmoPowerUpFromWeaponId] No config found for weaponId: ${weaponId}`);
      return null;
    }

    return new AmmoPowerUp(this, x, y, config.texture, {
      weaponId: config.weaponId,
      ammoAmount: ammoOverride ?? config.ammoAmount,
      weaponName: config.weaponName,
    });
  }

  /**
   * Initialize projectile system
   */
  private setupProjectileSystem(): void {
    // Load projectile C3 data (origins, etc.) from JSON cache
    DataManagerProjectile.load(this);

    // Load weapon data (guns, bombs, tractors) from JSON cache
    DataManagerWeapon.load(this);

    // Load powerup data (data-driven effects) from JSON cache
    DataManagerPowerUp.load(this);

    this.projectileManager = new RuntimeManagerProjectile(this);


    // Listen for tractor beam events (for gameplay logic)
    this.events.on('tractorBeamStarted', (_data: { weapon: any; position: { x: number; y: number } }) => {
      // Tractor beam started - gameplay logic here if needed
    });

    this.events.on('tractorBeamStopped', () => {
      // Tractor beam stopped - gameplay logic here if needed
    });
  }

  /**
   * Setup collision system colliders (projectiles, entities, player)
   * Note: CollisionSystem already created in setupCoreCollisionSystems()
   * Solid TileSprites already registered during level load
   */
  private setupCollisionSystem(): void {
    // Register projectile colliders (projectiles vs solidEntities for ground collision)
    this.collisionSystem.setupProjectileColliders(
      this.player,
      this.projectileManager.getFriendlyGroup(),
      this.projectileManager.getEnemyGroup(),
      this.projectileManager.getDestructibleEnemyGroup()
    );

    // Register solid entity colliders (blocks player and NPCs)
    this.collisionSystem.setupSolidColliders(this.player);

    // Register hazard colliders (bombs explode on player contact)
    this.collisionSystem.setupHazardColliders(this.player);
  }

  /**
   * Create weapon buttons UI
   */
  private createWeaponButtons(): void {
    this.weaponButtons = new WeaponButtonsBar({
      scene: this,
      y: 80,  // Distance from bottom (increased for taller buttons)
    });

    // Listen for ammo changes to update UI
    this.events.on('ammoChanged', (data: { slot: string; weaponId: string; ammo: number }) => {
      this.weaponButtons.updateAmmo(data.slot as any, data.ammo);
    });

    // Listen for weaponEmpty to show notification
    this.events.on('weaponEmpty', (data: { slot: string; weapon?: { name: string } }) => {
      const weaponName = data.weapon?.name ?? '---';
      FloatingNotification.warning(this, t('game.weapon_out_of_ammo').replace('{weapon}', weaponName), {
        position: { x: this.scale.width / 2, y: 150 },
        isHUD: true,
        fontSize: 20,
      });
    });

    // Listen for weapon swaps to update weapon name and ammo display
    this.events.on('weaponSwapped', (data: { slot: string; newWeapon?: { name: string } }) => {
      this.weaponButtons.updateWeaponName(data.slot as any, data.newWeapon?.name ?? '');

      // Also update ammo for the new weapon
      const slotType = data.slot as 'gun' | 'tractor' | 'bomb';
      const slot = this.player?.weapons?.getSlot(slotType);
      if (slot) {
        const ammo = slot.getAmmo();
        this.weaponButtons.updateAmmo(slotType, ammo);
      }
    });

    // Listen for player damage to show floating damage number
    this.events.on('playerDamaged', (data: { player: unknown; damage: number; health: number }) => {
      if (!this.player) return;
      FloatingNotification.damage(this, t('game.hp_damage').replace('{value}', `${data.damage}`), {
        target: this.player,
        offset: { x: 0, y: -60 },
      });
    });

    // Listen for player healed to show floating heal number
    this.events.on('playerHealed', (data: { player: unknown; amount: number; health: number }) => {
      if (!this.player) return;
      FloatingNotification.powerup(this, t('game.hp_heal').replace('{value}', `${data.amount}`), {
        target: this.player,
        offset: { x: 0, y: -60 },
      });
    });

    // Listen for weapon unlocked to show notification
    this.events.on('weaponUnlocked', (data: { weaponId: string; weapon: { name: string } }) => {
      FloatingNotification.success(this, t('game.new_weapon').replace('{weapon}', data.weapon.name), {
        position: { x: this.scale.width / 2, y: 150 },
        isHUD: true,
        fontSize: 20,
      });
    });

    // Update initial ammo and weapon names display
    if (this.player?.weapons) {
      this.initWeaponButtonsDisplay();
    }
  }

  /**
   * Initialize weapon buttons display (ammo and weapon names)
   */
  private initWeaponButtonsDisplay(): void {
    if (!this.player?.weapons) return;

    const slotTypes: Array<'gun' | 'tractor' | 'bomb'> = ['gun', 'tractor', 'bomb'];

    for (const slotType of slotTypes) {
      const slot = this.player.weapons.getSlot(slotType);
      if (!slot) continue;

      // Get active weapon info
      const weapon = slot.getActiveWeaponDefinition();
      const ammo = slot.getAmmo();

      // Update UI
      this.weaponButtons.updateWeaponName(slotType, weapon?.name ?? '');
      this.weaponButtons.updateAmmo(slotType, ammo);
    }
  }

  /**
   * Show notification when weapon swap fails
   */
  private showSwapFailedNotification(data: { slot: string; reason: string; message: string }): void {
    // Map slot string to SlotType
    const slotType = data.slot as 'gun' | 'tractor' | 'bomb';

    // Get button position for notification attachment
    const buttonPos = this.weaponButtons?.getButtonPosition(slotType);

    // Determine notification type based on reason
    const cooldownSeconds = (() => {
      // `RuntimeManagerWeapon` manda algo como: "Wait 2s"
      const m = /Wait\\s+(\\d+)s/i.exec(data.message ?? '');
      return m ? m[1] : null;
    })();

    const reasonConfig: Record<string, { text: string; type: 'error' | 'warning' | 'info' }> = {
      no_biomatter: { text: t('game.not_enough_biomatter'), type: 'error' },
      cooldown: {
        text: cooldownSeconds ? t('game.swap_cooldown').replace('{seconds}', cooldownSeconds) : data.message,
        type: 'warning',
      },
      single_weapon: { text: t('game.only_one_weapon'), type: 'info' },
    };

    const config = reasonConfig[data.reason] || { text: data.message, type: 'info' };

    // Show floating notification attached to the weapon button
    FloatingNotification.show(this, {
      text: config.text,
      type: config.type,
      position: buttonPos ?? { x: this.scale.width / 2, y: this.scale.height - 100 },
      offset: { x: 0, y: -60 },
      isHUD: true,
    });
  }

  /**
   * Create tractor beam visual
   */
  private createTractorBeam(): void {
    this.tractorBeam = new TractorBeam({
      scene: this,
      player: this.player,
    });

    // Connect tractor blocker events
    this.events.on('tractorBlockerAdded', (entity: import('../entities/base/Entity').Entity) => {
      this.tractorBeam?.registerBlocker(entity);
    });

    this.events.on('tractorBlockerRemoved', (entity: import('../entities/base/Entity').Entity) => {
      this.tractorBeam?.unregisterBlocker(entity);
    });
  }

  /**
   * Setup tractor beam behavior (connects beam with abduction)
   */
  private setupTractorBeamBehavior(): void {
    // Add TractorBeamBehavior to player
    this.tractorBeamBehavior = new TractorBeamBehavior(this.player, {
      tractorBeam: this.tractorBeam,
      abductableGroup: this.collisionSystem.getEnemyEntities(),
    });
    this.player.addBehavior(this.tractorBeamBehavior);

    // Listen for entity abduction completion
    this.events.on('entityAbducted', (data: { entity: import('../entities/base/Entity').Entity; bioMatter: number; weight: string }) => {
      const entityName = (data.entity as unknown as { prefabName?: string }).prefabName || data.entity.name || 'Unknown';
      const textureName = data.entity.texture?.key || 'no-texture';
      console.log(`[Abducted] ${entityName} (texture: ${textureName}) → +${data.bioMatter} biomatter | weight: ${data.weight}`);
      this.addBiomatter(data.bioMatter);
    });

    // Setup abduction effects (particles, floating text, sounds)
    this.abductionEffects = new AbductionEffects({
      scene: this,
      player: this.player,
    });

    // console.log('[Game] TractorBeamBehavior and AbductionEffects initialized');
  }

  /**
   * Add biomatter and update display
   */
  private addBiomatter(amount: number): void {
    this.currentBiomatter += amount;
    this.updateBiomatterDisplay();

    // Also update player's RuntimeManagerWeapon biomatter
    if (this.player?.weapons) {
      this.player.weapons.addBiomatter(amount);
    }
  }

  /**
   * Configura la cámara según el nivel
   */
  private setupCamera(level: LoadedLevel): void {
    // Bounds de la cámara (usando dimensiones efectivas con overrides)
    this.camera.setBounds(0, 0, level.effectiveWidth, level.effectiveHeight);

    // Centrar en el inicio del nivel (lado izquierdo, centrado verticalmente)
    this.camera.centerOn(this.scale.width / 2, level.effectiveHeight / 2);

    // Zoom para ver bien el nivel (ajustar según necesidad)
    // Para niveles muy anchos, mantener zoom 1
    this.camera.setZoom(1);
  }

  /**
   * Create the player UFO
   */
  private createPlayer(data: LevelData): void {
    // Spawn position: left side of level, vertically centered in play area
    const spawnX = 400;
    const spawnY = 400; // Upper area where UFO flies

    // Try to load player config from entities-c3data.json
    const prefabs = this.cache.json.get('entities-c3data') as { player?: Record<string, unknown> } | undefined;
    const playerPrefab = prefabs?.player;

    // Build config from prefab or use defaults
    const playerConfig = playerPrefab?.behaviors as Record<string, unknown> | undefined;

    this.player = new Player(this, spawnX, spawnY, {
      // Movement (from prefab or defaults)
      maxSpeed: (playerConfig?.eightDirection as Record<string, number>)?.maxSpeed ?? 800,
      acceleration: (playerConfig?.eightDirection as Record<string, number>)?.acceleration ?? 1200,
      deceleration: (playerConfig?.eightDirection as Record<string, number>)?.deceleration ?? 1000,
      // Health
      hp: (playerPrefab?.hp as number) ?? 15,
      maxHp: (playerPrefab?.maxHp as number) ?? 15,
      // Sine behaviors (from prefab or defaults)
      sineVertical: playerConfig?.sineVertical as Record<string, unknown> | undefined,
      sineHorizontal: playerConfig?.sineHorizontal as Record<string, unknown> | undefined,
    });

    // Set world bounds for player (keep within level)
    this.physics.world.setBounds(0, 0, data.width, data.height);

    // Camera follows player
    this.camera.startFollow(this.player, true, 0.1, 0.1);

    // Set deadzone so camera doesn't move on small movements
    this.camera.setDeadzone(200, 100);

    // Load weapons from storage loadout
    this.loadPlayerWeapons();

    // Register player in scene registry for access by entities (flee behavior, etc.)
    this.registry.set('player', this.player);

    // console.log('Player created at:', spawnX, spawnY);
    // if (playerPrefab) {
    //   console.log('Player config loaded from entities-c3data.json');
    // }
  }

  /**
   * Load weapons from storage into player's RuntimeManagerWeapon
   */
  private async loadPlayerWeapons(): Promise<void> {
    if (!this.player?.weapons) return;

    const weaponState = await storage.getWeaponState();

    // Add weapons to each slot from the loadout
    for (const [slot, loadout] of Object.entries(weaponState.loadout)) {
      const slotType = slot as 'gun' | 'tractor' | 'bomb';
      const weaponSlot = this.player.weapons.getSlot(slotType);
      if (!weaponSlot) continue;

      // Add each weapon in the loadout
      for (const weaponId of loadout.equipped) {
        const ammo = weaponState.ammo[weaponId] ?? 0;
        weaponSlot.addWeapon(weaponId, ammo);
      }

      // Set active weapon
      if (loadout.equipped.length > 0) {
        weaponSlot.setActiveWeapon(loadout.equipped[loadout.activeIndex]);
      }
    }

    // Update biomatter in RuntimeManagerWeapon
    const biomatter = await storage.getBiomatter();
    this.player.weapons.addBiomatter(biomatter - this.player.weapons.getBiomatter());

    // Update weapon buttons display now that weapons are loaded
    if (this.weaponButtons) {
      this.initWeaponButtonsDisplay();
    }
  }

  /**
   * Setup ground collision - prevents player from going through the floor
   * Uses solidEntities group from CollisionSystem (includes TileSprites with solid behavior)
   */
  private setupGroundCollision(): void {
    // Create collider between player and solid entities (ground TileSprites, etc.)
    this.groundCollider = this.physics.add.collider(
      this.player,
      this.collisionSystem.getSolidEntities()
    );

    // console.log('[Game] Player ground collision registered');
  }

  /**
   * Toggle player collision (Q key)
   */
  togglePlayerCollision(): void {
    this.playerCollisionEnabled = !this.playerCollisionEnabled;
    this.groundCollider.active = this.playerCollisionEnabled;

    // Also toggle world bounds collision
    const body = this.player.body as Phaser.Physics.Arcade.Body;
    body.setCollideWorldBounds(this.playerCollisionEnabled);

    console.log(`[DEBUG] Player collision: ${this.playerCollisionEnabled ? 'ON' : 'OFF'}`);
  }

  /**
   * Crea la UI del juego
   */
  private async createUI(data: LevelData): Promise<void> {
    const isDevMode = await storage.isDevModeEnabled();

    // Info del nivel (fixed, no se mueve con la cámara)
    this.levelInfoText = this.add.text(16, 16, `${data.name} (${data.id})`, {
      fontFamily: 'Arial',
      fontSize: '18px',
      color: '#ffffff',
      backgroundColor: '#000000aa',
      padding: { x: 8, y: 4 },
    });
    this.levelInfoText.setScrollFactor(0);
    this.levelInfoText.setDepth(LayerDepths.HUD);

    this.setDevUiVisible(isDevMode);

    // HUD - Vida con corazones (esquina superior izquierda)
    this.createHealthHUD();

    // HUD - Biomateria con icono (esquina superior derecha)
    this.createBiomatterHUD();

    // Cargar biomateria inicial
    this.loadBiomatterDisplay();
  }

  /**
   * Crea el HUD de vida con corazones
   */
  private createHealthHUD(): void {
    this.healthContainer = this.add.container(16, 84);
    this.healthContainer.setScrollFactor(0);
    this.healthContainer.setDepth(LayerDepths.HUD);
    this.healthHearts = [];

    // Fondo semi-transparente (se actualiza en updateHealthDisplay)
    this.healthBg = this.add.graphics();
    this.healthContainer.add(this.healthBg);
  }

  /**
   * Crea el HUD de biomateria con icono
   */
  private createBiomatterHUD(): void {
    const padding = 16;
    const iconScale = 1.5; // Icono es 20x20, escalamos a 30x30
    const yPos = 55;

    // Contenedor para biomateria
    const bioContainer = this.add.container(0, 0);
    bioContainer.setScrollFactor(0);
    bioContainer.setDepth(LayerDepths.HUD);

    // Fondo semi-transparente
    const bg = this.add.graphics();
    bg.fillStyle(0x000000, 0.67);
    bg.fillRoundedRect(this.scale.width - 130, yPos - 16, 114, 32, 6);
    bioContainer.add(bg);

    // Icono de biomateria
    this.biomatterIcon = this.add.image(this.scale.width - padding, yPos, 'hud_biomoney');
    this.biomatterIcon.setOrigin(1, 0.5);
    this.biomatterIcon.setScale(iconScale);
    bioContainer.add(this.biomatterIcon);

    // Texto de cantidad
    this.biomatterText = this.add.text(
      this.biomatterIcon.x - this.biomatterIcon.displayWidth - 8,
      yPos,
      '0',
      {
        fontFamily: 'Arial',
        fontSize: '20px',
        color: '#44ff88',
        fontStyle: 'bold',
      }
    );
    this.biomatterText.setOrigin(1, 0.5);
    bioContainer.add(this.biomatterText);
  }

  /**
   * Carga y muestra la biomateria desde storage
   */
  private async loadBiomatterDisplay(): Promise<void> {
    this.currentBiomatter = await storage.getBiomatter();
    this.updateBiomatterDisplay();
  }

  /**
   * Actualiza el display de biomateria
   */
  private updateBiomatterDisplay(): void {
    this.biomatterText.setText(this.currentBiomatter.toLocaleString());
  }

  /**
   * Actualiza el display de vida con corazones
   */
  private updateHealthDisplay(): void {
    if (!this.player || !this.healthContainer) return;

    const currentHealth = Math.max(0, Math.floor(this.player.health));

    // Solo actualizar si cambió la vida
    if (currentHealth === this.lastHealthDisplay) return;
    this.lastHealthDisplay = currentHealth;

    // Limpiar corazones anteriores
    this.healthHearts.forEach(heart => heart.destroy());
    this.healthHearts = [];

    // Crear corazones según la vida actual
    // Corazón original: 264x106, escalamos a ~32x13
    const heartScale = 0.12;
    const heartSpacing = 34;
    const padding = 8;

    // Actualizar fondo según cantidad de corazones
    this.healthBg.clear();
    if (currentHealth > 0) {
      const bgWidth = currentHealth * heartSpacing + padding * 2;
      this.healthBg.fillStyle(0x000000, 0.67);
      this.healthBg.fillRoundedRect(-padding, -16, bgWidth, 32, 6);
    }

    for (let i = 0; i < currentHealth; i++) {
      const heart = this.add.image(i * heartSpacing, 0, 'hud_heart');
      heart.setScale(heartScale);
      heart.setOrigin(0, 0.5);
      this.healthContainer.add(heart);
      this.healthHearts.push(heart);
    }
  }

  /**
   * Create collision debug overlay (SHIFT+F1 to toggle)
   */
  private createCollisionDebug(): void {
    this.collisionDebug = new CollisionDebugOverlay(this);
    this.collisionDebug.setPlayer(this.player);
    // Note: Ground collision now uses solidEntities (TileSprites with solid behavior)
    // CollisionDebugOverlay can be updated to show these if needed
  }

  /**
   * Create range debug overlay (SHIFT+F2 to toggle)
   */
  private createRangeDebug(): void {
    this.rangeDebug = new RangeDebugOverlay(this);
    this.rangeDebug.setPlayer(this.player);
  }

  /**
   * Create object inspector overlay (SHIFT+F3 to toggle)
   */
  private createObjectInspector(): void {
    if (!this.loadedLevel) return;

    this.objectInspector = new ObjectInspectorOverlay(this, this.loadedLevel);
  }

  /**
   * Create pause popup (ESC to toggle)
   */
  private createPausePopup(): void {
    this.pausePopup = new PausePopup({
      scene: this,
      onResume: () => {
        this.resumeGame();
      },
      onOptions: () => {
        this.resumeGame();
        this.scene.start('Configuration', { returnScene: 'Game' });
      },
      onAbandon: () => {
        this.resumeGame();
        this.scene.start('LevelSelect');
      },
      onExitGame: () => {
        this.resumeGame();
        this.scene.start('MainMenu');
      },
      onExit: () => {
        // Resume game state before exiting
        this.resumeGame();
        this.scene.start('LevelSelect');
      }
    });

    // ESC key to toggle pause
    this.input.keyboard!.on('keydown-ESC', () => {
      if (this.deathPopup?.getIsOpen()) return;
      if (this.pausePopup.getIsOpen()) {
        this.pausePopup.close();
        this.resumeGame();
      } else {
        this.pausePopup.open();  // Abrir popup antes de pausar tweens
        this.pauseGame();
      }
    });
  }

  /**
   * Create death popup (shown when player dies)
   */
  private createDeathPopup(): void {
    this.deathPopup = new DeathPopup({
      scene: this,
      onRestart: () => {
        // Resume game state before restarting (unpause animations, etc.)
        this.resumeGame();
        this.scene.restart({ levelId: this.levelId });
      },
      onExit: () => {
        // Resume game state before exiting
        this.resumeGame();
        this.scene.start('LevelSelect');
      }
    });

    // Listen for death animation complete to show popup
    this.events.on('playerDeathComplete', () => {
      // Small delay before showing popup
      this.time.delayedCall(300, () => {
        this.freezeForDeath();
        this.deathPopup.open();
      });
    });
  }

  private freezeForDeath(): void {
    if (this.isPaused) return;
    this.isPaused = true;
    this.physics.pause();
  }

  /**
   * Create boss health bar UI
   */
  private createBossHealthBar(): void {
    this.bossHealthBar = new BossHealthBar({ scene: this });
  }

  /**
   * Pause the game
   */
  private pauseGame(): void {
    if (this.isPaused) return;
    this.isPaused = true;

    // Pause physics
    this.physics.pause();

    // Pause tweens
    this.tweens.pauseAll();

    // Pause all animations
    this.anims.pauseAll();

    // Cambiar a música de pausa
    audioManager.playMusicForScene('Pause');

    console.log('[Game] Game paused');
  }

  /**
   * Resume the game
   */
  private resumeGame(): void {
    if (!this.isPaused) return;
    this.isPaused = false;

    // Resume physics
    this.physics.resume();

    // Resume tweens
    this.tweens.resumeAll();

    // Resume all animations
    this.anims.resumeAll();

    // Volver a la música del nivel actual
    const levelNum = parseInt(this.levelId.replace('level', ''));
    if (levelNum >= 1 && levelNum <= 7) {
      audioManager.playMusicForScene(`Level${levelNum}`);
    }
    this.loadLevelWinConfig();

    console.log('[Game] Game resumed');
  }

  /**
   * Configura controles de cámara para debug (solo zoom)
   */
  private setupCameraControls(): void {
    // Q = Toggle player collision (debug)
    this.input.keyboard!.on('keydown-Q', () => {
      this.togglePlayerCollision();
    });

    // E = Zoom in, R = Zoom out
    this.input.keyboard!.on('keydown-E', () => {
      this.camera.zoom = Math.min(2, this.camera.zoom + 0.1);
    });
    this.input.keyboard!.on('keydown-R', () => {
      this.camera.zoom = Math.max(0.25, this.camera.zoom - 0.1);
    });

    // SHIFT+F1 = CollisionDebugOverlay (hitboxes only) - handled by CollisionDebugOverlay itself
    // SHIFT+F2 = RangeDebugOverlay (flee, shooter, chase ranges) - handled by RangeDebugOverlay itself
    // SHIFT+F3 = ObjectInspectorOverlay (bounding boxes, object info) - handled by ObjectInspectorOverlay itself
    // SHIFT+F4 = ParticleDebug - handled below

    // K = Kill player (debug)
    this.input.keyboard!.on('keydown-K', () => {
      if (this.player && this.player.isAlive()) {
        console.log('[DEBUG] Killing player...');
        this.player.die();
      }
    });

    // SHIFT+F4 = Toggle particle debug display
    this.input.keyboard!.on('keydown-F4', (event: KeyboardEvent) => {
      if (event.shiftKey) {
        event.preventDefault();
        this.toggleParticleDebug();
      }
    });

    // SHIFT+F5 = Hot-reload override JSONs
    this.input.keyboard!.on('keydown-F5', (event: KeyboardEvent) => {
      if (event.shiftKey) {
        event.preventDefault();
        this.hotReloadData();
      }
    });

    // SHIFT+F6 = Toggle dev mode (all unlocked + high biomatter)
    this.input.keyboard!.on('keydown-F6', (event: KeyboardEvent) => {
      if (event.shiftKey) {
        event.preventDefault();
        this.toggleDevMode();
      }
    });
  }

  private async toggleDevMode(): Promise<void> {
    const enabled = await storage.toggleDevMode({ biomatter: 999999, maxLevel: 1 });
    await this.refreshPlayerStateFromStorage();
    this.player?.setDebugInvulnerable(enabled);
    this.setDevUiVisible(enabled);

    const mode = enabled ? 'ON' : 'OFF';
    FloatingNotification.info(this, `DEV MODE: ${mode}`, {
      position: { x: this.scale.width / 2, y: 110 },
      isHUD: true,
      fontSize: 24,
    });

    console.log(`[Game] Dev mode: ${mode}`);
  }

  private async refreshPlayerStateFromStorage(): Promise<void> {
    if (!this.player?.weapons) return;

    await this.loadPlayerWeapons();

    this.currentBiomatter = await storage.getBiomatter();
    this.updateBiomatterDisplay();
  }

  private setDevUiVisible(enabled: boolean): void {
    if (enabled) {
      if (!this.devInstructionsText) {
        this.devInstructionsText = this.add.text(16, 50, 'WASD: Move | Shift: Sprint | E/R: Zoom | Q: No-clip | Shift+F6: Dev Mode | Shift+F5: Reload Data', {
          fontFamily: 'Arial',
          fontSize: '14px',
          color: '#cccccc',
          backgroundColor: '#000000aa',
          padding: { x: 8, y: 4 },
        });
        this.devInstructionsText.setScrollFactor(0);
        this.devInstructionsText.setDepth(LayerDepths.HUD);
      }

      if (!this.backButton) {
        this.backButton = this.add.text(this.scale.width - 16, 16, '← Back', {
          fontFamily: 'Arial',
          fontSize: '18px',
          color: '#ffffff',
          backgroundColor: '#444444',
          padding: { x: 12, y: 6 },
        });
        this.backButton.setOrigin(1, 0);
        this.backButton.setScrollFactor(0);
        this.backButton.setDepth(LayerDepths.HUD);
        this.backButton.setInteractive({ useHandCursor: true });
        this.backButton.on('pointerover', () => this.backButton.setBackgroundColor('#666666'));
        this.backButton.on('pointerout', () => this.backButton.setBackgroundColor('#444444'));
        this.backButton.on('pointerdown', () => this.scene.start('LevelSelect'));
      }
      this.backButton.setVisible(true);
      this.devInstructionsText.setVisible(true);
      return;
    }

    if (this.devInstructionsText) this.devInstructionsText.setVisible(false);
    if (this.backButton) this.backButton.setVisible(false);
  }

  /**
   * Hot-reload override JSON files (SHIFT+F5)
   */
  private async hotReloadData(): Promise<void> {
    const success = await DebugManagerHotReload.reloadOverrides(this);
    DebugManagerHotReload.showNotification(this, success);
  }

  // Track shooter debug state
  private shooterDebugEnabled: boolean = false;

  /**
   * Toggle debug visualization for all shooter behaviors
   */
  private toggleShooterDebug(): void {
    this.shooterDebugEnabled = !this.shooterDebugEnabled;

    // Iterate over all active entities and toggle shooter debug
    for (const entity of this.spawnManager.getActiveEntities()) {
      const entityAny = entity as unknown as { behaviors?: Behavior[] };
      const shooterBehavior = entityAny.behaviors?.find(b => b instanceof ShooterBehavior) as ShooterBehavior | undefined;
      if (shooterBehavior) {
        shooterBehavior.setDebug(this.shooterDebugEnabled);
      }
    }

    console.log(`[Game] Shooter debug: ${this.shooterDebugEnabled ? 'ON' : 'OFF'}`);
  }

  /**
   * Toggle particle debug display
   */
  private toggleParticleDebug(): void {
    this.particleDebugEnabled = !this.particleDebugEnabled;

    if (!this.particleDebugText) {
      // Create the debug text
      this.particleDebugText = this.add.text(16, 100, '', {
        fontFamily: 'monospace',
        fontSize: '14px',
        color: '#00ffff',
        backgroundColor: '#000000aa',
        padding: { x: 8, y: 4 },
      });
      this.particleDebugText.setScrollFactor(0);
      this.particleDebugText.setDepth(LayerDepths.DebugOverlay);
    }

    this.particleDebugText.setVisible(this.particleDebugEnabled);
    console.log(`[Game] Particle debug: ${this.particleDebugEnabled ? 'ON' : 'OFF'} - Press F2 to toggle`);
  }

  /**
   * Update particle debug display
   */
  private updateParticleDebug(): void {
    if (!this.particleDebugEnabled || !this.particleDebugText) return;

    // Validate text object is still valid before updating
    if (!this.particleDebugText.active || !this.particleDebugText.canvas) {
      return;
    }

    try {
      const beamActive = this.tractorBeam?.getIsActive() ?? false;

      this.particleDebugText.setText([
        `[PARTICLE DEBUG] SHIFT+F4 toggle`,
        `Beam active: ${beamActive}`,
        ParticleRegistry.getDebugString(),
      ].join('\n'));
    } catch (e) {
      // Canvas was destroyed, recreate text object
      console.warn('[Game] Particle debug text corrupted, recreating...');
      this.particleDebugText.destroy();
      this.particleDebugText = null as unknown as Phaser.GameObjects.Text;
      this.particleDebugEnabled = false;
    }
  }

  private loadLevelWinConfig(): void {
    const raw = this.cache.json.get('level-win-config') as {
      default?: {
        bossesRequired?: number;
        edgeThreshold?: number;
        title?: string;
        subtitle?: string;
        continueText?: string;
        replayText?: string;
      };
      levels?: Record<string, {
        bossesRequired?: number;
        edgeThreshold?: number;
        title?: string;
        subtitle?: string;
        continueText?: string;
        replayText?: string;
      }>;
    } | undefined;

    const base = raw?.default ?? {};
    const level = raw?.levels?.[this.levelId] ?? {};
    this.levelWinConfig = {
      bossesRequired: level.bossesRequired ?? base.bossesRequired ?? 0,
      edgeThreshold: level.edgeThreshold ?? base.edgeThreshold ?? 24,
      title: level.title ?? base.title,
      subtitle: level.subtitle ?? base.subtitle,
      continueText: level.continueText ?? base.continueText,
      replayText: level.replayText ?? base.replayText,
    };
    if (this.levelWinConfig.bossesRequired <= 0) {
      this.victoryArmed = true;
    }
  }

  private checkLevelWinCondition(): void {
    if (this.hasWon || !this.player?.isAlive() || !this.loadedLevel) return;
    if (!this.victoryArmed) return;

    const rightEdge = this.loadedLevel.effectiveWidth - this.levelWinConfig.edgeThreshold;
    if (this.player.x < rightEdge) return;

    this.hasWon = true;
    this.isPaused = true;
    this.physics.pause();
    this.tweens.pauseAll();
    this.anims.pauseAll();
    this.player.freeze();

    this.scene.start('LevelWin', {
      levelId: this.levelId,
      levelNumber: parseInt(this.levelId.replace('level', ''), 10) || 1,
      biomatter: this.currentBiomatter,
      config: {
        title: this.levelWinConfig.title,
        subtitle: this.levelWinConfig.subtitle,
        continueText: this.levelWinConfig.continueText,
        replayText: this.levelWinConfig.replayText,
      },
    });
  }

  /**
   * Muestra mensaje de error si el nivel no carga
   */
  private showErrorMessage(): void {
    this.camera.setBackgroundColor(0x1a1a2e);

    this.add.text(this.scale.width / 2, this.scale.height / 2, 'Error loading level', {
      fontFamily: 'Arial',
      fontSize: '32px',
      color: '#ff5555',
    }).setOrigin(0.5);

    this.add.text(this.scale.width / 2, this.scale.height / 2 + 50, 'Press any key to go back', {
      fontFamily: 'Arial',
      fontSize: '18px',
      color: '#888888',
    }).setOrigin(0.5);

    this.input.keyboard!.once('keydown', () => {
      this.scene.start('LevelSelect');
    });
  }

  override update(time: number, delta: number): void {
    if (!this.loadedLevel) return;

    // Skip updates when paused (but allow debug overlays)
    if (this.isPaused) {
      // Only update debug overlays when paused
      if (this.collisionDebug) this.collisionDebug.update();
      if (this.rangeDebug) this.rangeDebug.update();
      if (this.objectInspector) this.objectInspector.update();
      return;
    }

    // Update player
    if (this.player && this.player.isAlive()) {
      this.player.update(time, delta);

      // Adjust camera follow speed based on sprint
      if (this.player.isSprinting()) {
        this.camera.setLerp(1, 1);      // Instant follow when sprinting
      } else {
        this.camera.setLerp(0.1, 0.1);  // Normal smooth follow
      }

      // Update HUD health display
      this.updateHealthDisplay();
    }

    // Update spawn system
    if (this.spawnManager) {
      this.spawnManager.update(time, delta);

      // Update all active entities (including bosses)
      for (const entity of this.spawnManager.getActiveEntities()) {
        if (entity.active) {
          entity.update(time, delta);
        }
      }
    }

    // Update projectile system
    if (this.projectileManager) {
      this.projectileManager.update(time, delta);
    }

    // Update AOE zones
    if (this.collisionSystem) {
      this.collisionSystem.updateAoeZones(time, delta);
    }

    // Update tractor beam visual
    if (this.tractorBeam) {
      this.tractorBeam.update(time, delta);
    }

    // Note: TractorBeamBehavior is updated automatically via Player.update() -> Entity.preUpdate()

    // Update debug overlays
    if (this.collisionDebug) {
      this.collisionDebug.update();
    }
    if (this.rangeDebug) {
      this.rangeDebug.update();
    }
    if (this.objectInspector) {
      this.objectInspector.update();
    }

    // Update boss health bar
    if (this.bossHealthBar) {
      this.bossHealthBar.update();
    }

    // Update particle debug display
    this.updateParticleDebug();

    // Check end-of-level victory trigger
    this.checkLevelWinCondition();
  }

}
